import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Trophy, ChevronLeft, Crown, Medal } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { api } from '@/lib/api/client';

interface LeaderboardEntry {
  rank: number;
  scoutId: string;
  name: string | null;
  avatarUrl: string | null;
  level: number;
  xpEarned: number;
}

interface LeaderboardData {
  entries: LeaderboardEntry[];
  period: string;
  buildingId: string | null;
  generatedAt: string;
}

interface RankData {
  rank: number | null;
  xpEarned: number;
  total: number;
}

const PERIODS = ['DAILY', 'WEEKLY', 'MONTHLY', 'ALL_TIME'] as const;

export default function LeaderboardPage() {
  const { t } = useTranslation('scout');
  const navigate = useNavigate();
  const { isAuthenticated, scout } = useAuthStore();
  const [leaderboard, setLeaderboard] = useState<LeaderboardData | null>(null);
  const [myRank, setMyRank] = useState<RankData | null>(null);
  const [period, setPeriod] = useState<string>('WEEKLY');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
      return;
    }
    loadLeaderboard();
  }, [isAuthenticated, navigate, period]);

  async function loadLeaderboard() {
    setLoading(true);
    setLoadError(false);
    try {
      const [lb, rank] = await Promise.all([
        api.get<LeaderboardData>(`/scout/leaderboard?period=${period}`),
        api.get<RankData>(`/scout/leaderboard/rank?period=${period}`),
      ]);
      setLeaderboard(lb);
      setMyRank(rank);
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gold/5 to-white pb-24">
      {/* Header */}
      <div className="px-4 pt-6 pb-4 flex items-center gap-3">
        <button onClick={() => navigate('/')} className="p-2 -ms-2 rounded-lg hover:bg-warm-faint">
          <ChevronLeft className="w-5 h-5 text-muted-foreground" />
        </button>
        <div>
          <h1 className="text-section font-bold text-foreground">{t('leaderboard.title')}</h1>
          <p className="text-small text-muted-foreground">{t('leaderboard.subtitle')}</p>
        </div>
      </div>

      {/* Period tabs */}
      <div className="px-4 mb-4 flex gap-1.5 overflow-x-auto pb-1">
        {PERIODS.map((p) => (
          <button
            key={p}
            onClick={() => {
              setPeriod(p);
              window.gtag?.('event', 'leaderboard_period_change', { period: p });
            }}
            className={`px-3 py-2 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
              period === p ? 'bg-gold text-white' : 'bg-muted text-muted-foreground hover:bg-warm-faint'
            }`}
          >
            {t(`leaderboard.period.${p}`)}
          </button>
        ))}
      </div>

      {/* My rank card */}
      {myRank &&
        (myRank.rank ? (
          <div className="mx-4 mb-4">
            <div className="p-4 rounded-xl bg-gradient-to-r from-gold to-gold/80 text-white shadow-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-white/70">{t('leaderboard.yourRank')}</p>
                  <p className="text-page-title">#{myRank.rank}</p>
                </div>
                <div className="text-end">
                  <p className="text-xs text-white/70">{t('leaderboard.xpEarned')}</p>
                  <p className="text-section font-semibold">{myRank.xpEarned} XP</p>
                </div>
              </div>
            </div>
            {/* Motivational hint: show gap to the rank above */}
            {myRank.rank > 1 &&
              leaderboard &&
              (() => {
                const above = leaderboard.entries.find((e) => e.rank === myRank.rank! - 1);
                if (above && above.xpEarned > myRank.xpEarned) {
                  const gap = above.xpEarned - myRank.xpEarned;
                  return (
                    <p className="text-caption text-gold text-center mt-2">
                      {t('leaderboard.xpToClimb', { xp: gap, rank: myRank.rank - 1 })}
                    </p>
                  );
                }
                return null;
              })()}
          </div>
        ) : (
          <div className="mx-4 mb-4 p-4 rounded-xl bg-muted border border-warm-border text-center">
            <p className="text-small font-medium text-foreground">{t('leaderboard.notRanked')}</p>
            <p className="text-caption text-muted-foreground mt-1">{t('leaderboard.notRankedHint')}</p>
          </div>
        ))}

      {/* Leaderboard list */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin w-8 h-8 border-2 border-gold border-t-transparent rounded-full" />
        </div>
      ) : loadError ? (
        <div className="text-center py-12">
          <Trophy className="w-12 h-12 text-steel mx-auto mb-3" />
          <p className="text-small text-muted-foreground mb-4">{t('leaderboard.loadError')}</p>
          <button
            onClick={loadLeaderboard}
            className="px-5 py-2.5 rounded-xl bg-muted text-small font-medium text-foreground"
          >
            {t('leaderboard.retry')}
          </button>
        </div>
      ) : leaderboard && leaderboard.entries.length > 0 ? (
        <div className="mx-4 space-y-2">
          {leaderboard.entries.map((entry) => (
            <LeaderboardRow key={entry.scoutId} entry={entry} isMe={entry.scoutId === scout?.id} t={t} />
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <Trophy className="w-12 h-12 text-steel mx-auto mb-3" />
          <p className="text-muted-foreground">{t('leaderboard.empty')}</p>
        </div>
      )}
    </div>
  );
}

function LeaderboardRow({
  entry,
  isMe,
  t,
}: {
  entry: LeaderboardEntry;
  isMe: boolean;
  t: (key: string, opts?: Record<string, unknown>) => string;
}) {
  const rankIcon =
    entry.rank === 1 ? (
      <Crown className="w-5 h-5 text-gold" />
    ) : entry.rank === 2 ? (
      <Medal className="w-5 h-5 text-steel" />
    ) : entry.rank === 3 ? (
      <Medal className="w-5 h-5 text-gold/70" />
    ) : null;

  return (
    <div
      className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${
        isMe ? 'bg-gold/5 border-gold/20 ring-1 ring-gold/30' : 'bg-white border-warm-border'
      }`}
    >
      {/* Rank */}
      <div className="w-8 flex items-center justify-center">
        {rankIcon || <span className="text-small font-bold text-muted-foreground">#{entry.rank}</span>}
      </div>

      {/* Avatar */}
      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary to-forest flex items-center justify-center text-white text-small font-bold shrink-0">
        {entry.name?.[0]?.toUpperCase() || '?'}
      </div>

      {/* Name & level */}
      <div className="flex-1 min-w-0">
        <p className={`text-small font-semibold truncate ${isMe ? 'text-foreground' : 'text-foreground'}`}>
          {entry.name || t('leaderboard.anonymous')}
          {isMe && <span className="text-caption text-gold ms-1">({t('leaderboard.you')})</span>}
        </p>
        <p className="text-caption text-muted-foreground">{t('home.level', { level: entry.level })}</p>
      </div>

      {/* XP */}
      <div className="text-end">
        <p className="text-small font-bold text-foreground">{entry.xpEarned}</p>
        <p className="text-[10px] text-muted-foreground">XP</p>
      </div>
    </div>
  );
}
